interface MediaFile {
  fileId: string;
  s3Key: string;
  s3Bucket: string;
  fileType: 'image' | 'video';
  fileName: string;
  fileSize: number;
}

export interface WeatherReport {
  reportId: string;
  slug?: string;
  title: string;
  content: string;
  authorName: string;
  mediaFiles: MediaFile[];
  createdAt: string;
  updatedAt: string;
}

export interface Photo {
  photoId: string;
  title?: string;
  description: string;
  author?: string;
  authorName: string;
  mediaFile: {
    fileId: string;
    s3Key: string;
    s3Bucket: string;
    fileType: 'image';
    fileName: string;
    fileSize: number;
  };
  published: string; // "true" or "false" (string for DynamoDB GSI)
  createdAt: string;
  updatedAt: string;
}

export interface BlogPost {
  postId: string;
  title: string;
  content: string;
  excerpt?: string;
  author?: string;
  authorName: string;
  tags?: string[];
  mediaFiles: Array<{
    fileId: string;
    s3Key: string;
    s3Bucket: string;
    fileName: string;
    altText?: string;
    caption?: string;
  }>;
  published: string; // "true" or "false" (string for DynamoDB GSI)
  featured: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface Video {
  videoId: string;
  title: string;
  description?: string;
  youtubeUrl: string;
  youtubeId: string;
  thumbnailUrl?: string;
  authorName: string;
  tags?: string[];
  published: string; // "true" or "false" (string for DynamoDB GSI)
  featured: string; // "true" or "false" (string for DynamoDB GSI)
  createdAt: string;
  updatedAt: string;
}

export interface ReportsResponse {
  reports: WeatherReport[];
  nextToken?: string;
}

// ===== DOROTHY (WEATHER STATION) INTERFACES =====

export interface WeatherReading {
  PK: string;
  SK: string;
  readingId: string;
  stationId: string;
  timestamp: string;
  rawData: {
    temperature?: number;
    humidity?: number;
    pressure?: number;
    windSpeed?: number;
    windDirection?: number;
    windGust?: number;
    precipitation?: number;
    precipitationDaily?: number;
    solarRadiation?: number;
    uvIndex?: number;
    indoorTemperature?: number;
    indoorHumidity?: number;
    batteryLevel?: number;
    sensorTimestamp?: number;
    [key: string]: any;
  };
  calculatedMetrics?: {
    dewPoint?: number;
    heatIndex?: number;
    windChill?: number;
    feelsLike?: number;
    absoluteHumidity?: number;
    precipitationDaily9AM?: number;
    precipitation6Hours?: number;
    precipitationYesterday9AM?: number;
    temperatureTrend?: string;
    pressureTrend?: string;
    dataQuality?: string;
  };
  status: string;
  apiResponseStatus?: number;
  errors?: any[];
  fetchedAt: string;
  createdAt: string;
  expirationTime?: number;
  connectionStatus?: {
    status: 'online' | 'offline' | 'degraded' | 'unknown';
    dataAgeMinutes: number;
    lastSensorUpdate: string | null;
    message: string;
  };
  trends?: {
    [key: string]: {
      delta: number;
      direction: 'up' | 'down' | 'stable';
    };
  };
}

export interface WeatherHistoryResponse {
  items: WeatherReading[];
  count: number;
  nextToken?: string;
}

export interface ExportDorothyTelemetryRequest {
  startDate: string; // ISO 8601
  endDate: string;   // ISO 8601
}

export interface ExportDorothyTelemetryResponse {
  downloadUrl: string;
  fileName: string;
  recordCount: number;
  expiresIn: number;
  timestamp: string;
}

export interface RadarImage {
  fileName: string;
  timestamp: string; // ISO 8601 UTC
  timestampArgentina: string; // ISO 8601 Argentina (UTC-3)
  ageMinutes: number;
  url: string;
}

export interface GetRadarImagesResponse {
  images: RadarImage[];
  count: number;
  timestamp: string;
}

export interface GetLatestRadarImageResponse {
  image: RadarImage | null;
  timestamp: string;
}

export interface ApiError {
  code: string;
  message: string;
  details?: object;
  timestamp: string;
}

export interface AuthResponse {
  accessToken: string;
  refreshToken: string;
  idToken: string;
}

// Error types for better error handling
export enum ErrorType {
  NETWORK_ERROR = 'NETWORK_ERROR',
  AUTHENTICATION_ERROR = 'AUTHENTICATION_ERROR',
  VALIDATION_ERROR = 'VALIDATION_ERROR',
  SERVER_ERROR = 'SERVER_ERROR',
  TIMEOUT_ERROR = 'TIMEOUT_ERROR',
  UNKNOWN_ERROR = 'UNKNOWN_ERROR'
}

export class ApiClientError extends Error {
  public readonly type: ErrorType;
  public readonly code: string;
  public readonly details?: object;
  public readonly timestamp: string;

  constructor(
    message: string,
    type: ErrorType = ErrorType.UNKNOWN_ERROR,
    code: string = 'UNKNOWN',
    details?: object
  ) {
    super(message);
    this.name = 'ApiClientError';
    this.type = type;
    this.code = code;
    this.details = details;
    this.timestamp = new Date().toISOString();
  }
}

// Retry configuration
interface RetryConfig {
  maxRetries: number;
  baseDelay: number;
  maxDelay: number;
  retryableStatuses: number[];
}

// Request interceptor type
type RequestInterceptor = (config: RequestInit & { url: string }) => RequestInit & { url: string };

// Response interceptor type
type ResponseInterceptor = (response: Response) => Promise<Response>;

class ApiClient {
  private baseUrl: string;
  private getAuthToken: (() => string | null) | null = null;
  private onTokenExpired: (() => Promise<boolean>) | null = null;
  private requestInterceptors: RequestInterceptor[] = [];
  private responseInterceptors: ResponseInterceptor[] = [];
  private retryConfig: RetryConfig = {
    maxRetries: 3,
    baseDelay: 1000,
    maxDelay: 10000,
    retryableStatuses: [408, 429, 500, 502, 503, 504]
  };

  constructor() {
    // In production, this would be the actual API Gateway URL
    // For now, we'll use a placeholder that can be configured via environment
    this.baseUrl = import.meta.env.VITE_API_URL || '/api';
  }

  /**
   * Set authentication token getter and refresh callback
   */
  setAuthHandlers(
    getToken: () => string | null,
    onExpired: () => Promise<boolean>
  ) {
    this.getAuthToken = getToken;
    this.onTokenExpired = onExpired;
  }

  /**
   * Add a request interceptor
   */
  addRequestInterceptor(interceptor: RequestInterceptor): void {
    this.requestInterceptors.push(interceptor);
  }

  /**
   * Add a response interceptor
   */
  addResponseInterceptor(interceptor: ResponseInterceptor): void {
    this.responseInterceptors.push(interceptor);
  }

  /**
   * Configure retry behavior
   */
  setRetryConfig(config: Partial<RetryConfig>): void {
    this.retryConfig = { ...this.retryConfig, ...config };
  }

  /**
   * Calculate exponential backoff delay with jitter
   */
  private calculateDelay(attempt: number): number {
    const exponentialDelay = Math.min(
      this.retryConfig.baseDelay * Math.pow(2, attempt),
      this.retryConfig.maxDelay
    );
    // Add jitter (random value between 0 and delay)
    return Math.random() * exponentialDelay;
  }

  /**
   * Sleep for specified milliseconds
   */
  private sleep(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  /**
   * Check if error is retryable
   */
  private isRetryable(status: number): boolean {
    return this.retryConfig.retryableStatuses.includes(status);
  }

  /**
   * Parse error response
   */
  private async parseErrorResponse(response: Response): Promise<ApiClientError> {
    try {
      const errorData: any = await response.json();
      
      // Check if response follows our error format
      if (errorData && typeof errorData === 'object' && errorData.error) {
        return new ApiClientError(
          errorData.error.message || `Request failed with status ${response.status}`,
          this.mapStatusToErrorType(response.status),
          errorData.error.code || 'UNKNOWN',
          errorData.error.details
        );
      }
      
      // Fallback for non-standard error responses
      return new ApiClientError(
        errorData?.message || `Request failed with status ${response.status}`,
        this.mapStatusToErrorType(response.status),
        'HTTP_ERROR'
      );
    } catch {
      // If JSON parsing fails, return generic error
      return new ApiClientError(
        `HTTP ${response.status}: ${response.statusText}`,
        this.mapStatusToErrorType(response.status),
        'HTTP_ERROR'
      );
    }
  }

  /**
   * Map HTTP status to error type
   */
  private mapStatusToErrorType(status: number): ErrorType {
    if (status === 401 || status === 403) {
      return ErrorType.AUTHENTICATION_ERROR;
    }
    if (status === 400 || status === 422) {
      return ErrorType.VALIDATION_ERROR;
    }
    if (status >= 500) {
      return ErrorType.SERVER_ERROR;
    }
    if (status === 408) {
      return ErrorType.TIMEOUT_ERROR;
    }
    return ErrorType.UNKNOWN_ERROR;
  }

  /**
   * Execute request with retry logic
   */
  private async executeWithRetry<T>(
    url: string,
    config: RequestInit,
    requiresAuth: boolean
  ): Promise<T> {
    let lastError: ApiClientError | null = null;
    
    for (let attempt = 0; attempt <= this.retryConfig.maxRetries; attempt++) {
      try {
        // Apply request interceptors
        let interceptedConfig = { ...config, url };
        for (const interceptor of this.requestInterceptors) {
          interceptedConfig = interceptor(interceptedConfig);
        }

        const response = await fetch(interceptedConfig.url, interceptedConfig);
        
        // Apply response interceptors
        let interceptedResponse = response;
        for (const interceptor of this.responseInterceptors) {
          interceptedResponse = await interceptor(interceptedResponse);
        }

        // Handle authentication errors
        if (interceptedResponse.status === 401 && requiresAuth && this.onTokenExpired) {
          const refreshed = await this.onTokenExpired();
          if (refreshed) {
            // Retry with new token
            const newToken = this.getAuthToken?.();
            if (newToken) {
              const headers = new Headers(config.headers);
              headers.set('Authorization', `Bearer ${newToken}`);
              const retryConfig = { ...config, headers };
              const retryResponse = await fetch(url, retryConfig);
              
              if (retryResponse.ok) {
                return await retryResponse.json() as T;
              }
              
              throw await this.parseErrorResponse(retryResponse);
            }
          }
          throw new ApiClientError(
            'Authentication required',
            ErrorType.AUTHENTICATION_ERROR,
            'AUTH_REQUIRED'
          );
        }

        // Check if response is successful
        if (interceptedResponse.ok) {
          return await interceptedResponse.json() as T;
        }

        // Parse error
        const error = await this.parseErrorResponse(interceptedResponse);
        
        // Check if we should retry
        if (attempt < this.retryConfig.maxRetries && this.isRetryable(interceptedResponse.status)) {
          lastError = error;
          const delay = this.calculateDelay(attempt);
          await this.sleep(delay);
          continue;
        }

        throw error;
      } catch (error) {
        // Handle network errors (fetch failures)
        if (error instanceof TypeError && (error.message.includes('fetch') || error.message.includes('Network'))) {
          lastError = new ApiClientError(
            'Network error. Please check your connection.',
            ErrorType.NETWORK_ERROR,
            'NETWORK_ERROR'
          );
          
          // Retry network errors
          if (attempt < this.retryConfig.maxRetries) {
            const delay = this.calculateDelay(attempt);
            await this.sleep(delay);
            continue;
          }
          
          throw lastError;
        }
        
        // If it's already an ApiClientError, throw it
        if (error instanceof ApiClientError) {
          throw error;
        }
        
        // For other errors, wrap them
        throw new ApiClientError(
          error instanceof Error ? error.message : 'An unexpected error occurred',
          ErrorType.UNKNOWN_ERROR,
          'UNKNOWN'
        );
      }
    }

    // If we exhausted all retries, throw the last error
    throw lastError || new ApiClientError(
      'Request failed after multiple retries',
      ErrorType.NETWORK_ERROR,
      'MAX_RETRIES_EXCEEDED'
    );
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {},
    requiresAuth: boolean = false
  ): Promise<T> {
    const url = `${this.baseUrl}${endpoint}`;
    
    const headers: Record<string, string> = {
      ...options.headers as Record<string, string>,
    };

    // Only set Content-Type if not already specified and not FormData
    if (!headers['Content-Type'] && !headers['content-type'] && !(options.body instanceof FormData)) {
      headers['Content-Type'] = 'application/json';
    }

    // Add authorization header if required and available
    if (requiresAuth && this.getAuthToken) {
      const token = this.getAuthToken();
      if (token) {
        headers.Authorization = `Bearer ${token}`;
      }
    }

    const config: RequestInit = {
      ...options,
      headers,
    };

    return this.executeWithRetry<T>(url, config, requiresAuth);
  }

  /**
   * Authenticate user with username and password
   */
  async login(credentials: {
    username: string;
    password: string;
  }): Promise<AuthResponse> {
    return this.request<AuthResponse>(
      '/auth/login',
      {
        method: 'POST',
        body: JSON.stringify(credentials),
      }
    );
  }

  /**
   * Refresh authentication token
   */
  async refreshToken(refreshToken: string): Promise<AuthResponse> {
    return this.request<AuthResponse>(
      '/auth/refresh',
      {
        method: 'POST',
        body: JSON.stringify({ refreshToken }),
      }
    );
  }

  /**
   * Fetch published weather reports with pagination support
   */
  async getReports(limit: number = 20, nextToken?: string): Promise<ReportsResponse> {
    const params = new URLSearchParams();
    params.append('limit', limit.toString());
    
    if (nextToken) {
      params.append('nextToken', nextToken);
    }

    return this.request<ReportsResponse>(`/reports?${params.toString()}`);
  }

  /**
   * Fetch a specific weather report by ID
   */
  async getReport(reportId: string): Promise<WeatherReport> {
    const response = await this.request<{ report: WeatherReport }>(`/reports/${reportId}`);
    return response.report;
  }

  /**
   * Get presigned URL for uploading a file to S3
   */
  async getReportUploadUrl(file: File): Promise<{
    uploadUrl: string;
    fileId: string;
    s3Key: string;
  }> {
    const params = new URLSearchParams({
      fileName: file.name,
      fileType: file.type,
      fileSize: file.size.toString(),
    });

    return this.request<{
      uploadUrl: string;
      fileId: string;
      s3Key: string;
    }>(`/reports/upload-url?${params.toString()}`, {}, true);
  }

  /**
   * Create a new weather report (requires authentication)
   */
  async createReport(reportData: {
    title: string;
    content: string;
    mediaFiles?: File[];
  }): Promise<{ reportId: string; status: string }> {
    // Upload files to S3 first
    const uploadedFiles: Array<{
      fileId: string;
      s3Key: string;
      fileName: string;
      fileType: 'image' | 'video';
      fileSize: number;
    }> = [];

    if (reportData.mediaFiles && reportData.mediaFiles.length > 0) {
      for (const file of reportData.mediaFiles) {
        // Get presigned URL
        const { uploadUrl, fileId, s3Key } = await this.getReportUploadUrl(file);

        // Upload file directly to S3
        await this.uploadFileToS3(uploadUrl, file);

        // Add file reference
        uploadedFiles.push({
          fileId,
          s3Key,
          fileName: file.name,
          fileType: file.type.startsWith('image/') ? 'image' : 'video',
          fileSize: file.size,
        });
      }
    }

    // Create report with S3 references
    return this.request<{ reportId: string; status: string }>(
      '/reports',
      {
        method: 'POST',
        body: JSON.stringify({
          title: reportData.title,
          content: reportData.content,
          published: true,
          mediaFiles: uploadedFiles,
        }),
      },
      true
    );
  }

  /**
   * Update an existing weather report (requires authentication)
   */
  async updateReport(
    reportId: string,
    reportData: {
      title: string;
      content: string;
      mediaFiles?: File[];
    }
  ): Promise<{ status: string }> {
    const formData = new FormData();
    formData.append('title', reportData.title);
    formData.append('content', reportData.content);
    formData.append('published', 'true'); // Por defecto, los reportes se publican
    
    if (reportData.mediaFiles) {
      reportData.mediaFiles.forEach((file, index) => {
        formData.append(`mediaFile_${index}`, file);
      });
    }

    return this.request<{ status: string }>(
      `/reports/${reportId}`,
      {
        method: 'PUT',
        body: formData,
        headers: {}, // Don't set Content-Type for FormData
      },
      true
    );
  }

  /**
   * Delete a weather report (requires authentication)
   */
  async deleteReport(reportId: string): Promise<{ status: string }> {
    return this.request<{ status: string }>(
      `/reports/${reportId}`,
      {
        method: 'DELETE',
      },
      true
    );
  }

  /**
   * Get all users (requires authentication)
   */
  async getUsers(): Promise<{ users: any[] }> {
    return this.request<{ users: any[] }>('/users', {}, true);
  }

  /**
   * Create a new user (requires authentication)
   */
  async createUser(userData: {
    email: string;
    name: string;
    role: 'admin' | 'editor';
  }): Promise<{ userId: string; status: string }> {
    return this.request<{ userId: string; status: string }>(
      '/users',
      {
        method: 'POST',
        body: JSON.stringify(userData),
      },
      true
    );
  }

  /**
   * Update a user (requires authentication)
   */
  async updateUser(
    userId: string,
    userData: {
      name?: string;
      role?: 'admin' | 'editor';
      active?: boolean;
    }
  ): Promise<{ status: string }> {
    return this.request<{ status: string }>(
      `/users/${userId}`,
      {
        method: 'PUT',
        body: JSON.stringify(userData),
      },
      true
    );
  }

  /**
   * Deactivate a user (requires authentication)
   */
  async deactivateUser(userId: string): Promise<{ status: string }> {
    return this.request<{ status: string }>(
      `/users/${userId}`,
      {
        method: 'DELETE',
      },
      true
    );
  }

  /**
   * Get audit logs (requires authentication)
   */
  async getAuditLogs(params?: {
    startDate?: string;
    endDate?: string;
    userId?: string;
  }): Promise<{ logs: any[] }> {
    const searchParams = new URLSearchParams();
    
    if (params?.startDate) {
      searchParams.append('startDate', params.startDate);
    }
    if (params?.endDate) {
      searchParams.append('endDate', params.endDate);
    }
    if (params?.userId) {
      searchParams.append('userId', params.userId);
    }

    const queryString = searchParams.toString();
    const endpoint = queryString ? `/audit-logs?${queryString}` : '/audit-logs';
    
    return this.request<{ logs: any[] }>(endpoint, {}, true);
  }

  // ===== PHOTO ENDPOINTS =====

  /**
   * Get all photos (public endpoint)
   */
  async getPhotos(params?: {
    limit?: number;
    lastEvaluatedKey?: string;
    published?: boolean;
  }): Promise<{
    photos: Photo[];
    lastEvaluatedKey?: string;
    hasMore: boolean;
  }> {
    const searchParams = new URLSearchParams();
    
    if (params?.limit) {
      searchParams.append('limit', params.limit.toString());
    }
    if (params?.lastEvaluatedKey) {
      searchParams.append('lastEvaluatedKey', params.lastEvaluatedKey);
    }
    if (params?.published !== undefined) {
      searchParams.append('published', params.published.toString());
    }

    const queryString = searchParams.toString();
    const endpoint = queryString ? `/photos?${queryString}` : '/photos';
    
    return this.request<{
      photos: Photo[];
      lastEvaluatedKey?: string;
      hasMore: boolean;
    }>(endpoint);
  }

  /**
   * Get presigned URL for photo upload (requires authentication)
   */
  async getPhotoUploadUrl(uploadData: {
    fileName: string;
    fileType: string;
    fileSize: number;
  }): Promise<{
    uploadUrl: string;
    fileId: string;
    s3Key: string;
  }> {
    return this.request<{
      uploadUrl: string;
      fileId: string;
      s3Key: string;
    }>(
      '/photos/upload-url',
      {
        method: 'POST',
        body: JSON.stringify(uploadData),
      },
      true
    );
  }

  /**
   * Upload file directly to S3 using presigned URL
   */
  async uploadFileToS3(uploadUrl: string, file: File): Promise<void> {
    const response = await fetch(uploadUrl, {
      method: 'PUT',
      body: file,
      headers: {
        'Content-Type': file.type,
      },
    });

    if (!response.ok) {
      throw new ApiClientError(
        `Failed to upload file: ${response.status} ${response.statusText}`,
        ErrorType.SERVER_ERROR,
        'UPLOAD_FAILED'
      );
    }
  }

  /**
   * Create photo record after S3 upload (requires authentication)
   */
  async createPhotoFromUpload(photoData: {
    title?: string;
    description: string;
    author?: string;
    published?: boolean;
    fileId: string;
    s3Key: string;
    fileName: string;
    fileSize: number;
    fileType: string;
  }): Promise<{ photoId: string; status: string; photo: any }> {
    return this.request<{ photoId: string; status: string; photo: any }>(
      '/photos/from-upload',
      {
        method: 'POST',
        body: JSON.stringify(photoData),
      },
      true
    );
  }

  /**
   * Create a new photo using presigned URL workflow (requires authentication)
   */
  async createPhotoWithPresignedUrl(photoData: {
    title?: string;
    description: string;
    author?: string;
    published?: boolean;
    file: File;
  }): Promise<{ photoId: string; status: string }> {
    // Step 1: Get presigned URL
    const uploadUrlResponse = await this.getPhotoUploadUrl({
      fileName: photoData.file.name,
      fileType: photoData.file.type,
      fileSize: photoData.file.size,
    });

    // Step 2: Upload file to S3
    await this.uploadFileToS3(uploadUrlResponse.uploadUrl, photoData.file);

    // Step 3: Create photo record
    const createResponse = await this.createPhotoFromUpload({
      title: photoData.title,
      description: photoData.description,
      author: photoData.author,
      published: photoData.published,
      fileId: uploadUrlResponse.fileId,
      s3Key: uploadUrlResponse.s3Key,
      fileName: photoData.file.name,
      fileSize: photoData.file.size,
      fileType: photoData.file.type,
    });

    return {
      photoId: createResponse.photoId,
      status: createResponse.status,
    };
  }

  /**
   * Create a new photo (requires authentication)
   * This method now uses presigned URLs by default for better performance
   */
  async createPhoto(photoData: {
    title?: string;
    description: string;
    author?: string;
    published?: boolean;
    file: File;
  }): Promise<{ photoId: string; status: string }> {
    // Use presigned URL workflow for better performance and to bypass API Gateway limits
    return this.createPhotoWithPresignedUrl(photoData);
  }

  /**
   * Update an existing photo (requires authentication)
   */
  async updatePhoto(
    photoId: string,
    photoData: {
      title?: string;
      description?: string;
      author?: string;
      published?: boolean;
    }
  ): Promise<Photo> {
    return this.request<Photo>(
      `/photos/${photoId}`,
      {
        method: 'PUT',
        body: JSON.stringify(photoData),
      },
      true
    );
  }

  /**
   * Delete a photo (requires authentication)
   */
  async deletePhoto(photoId: string): Promise<{ message: string; photoId: string }> {
    return this.request<{ message: string; photoId: string }>(
      `/photos/${photoId}`,
      {
        method: 'DELETE',
      },
      true
    );
  }

  // ===== BLOG POST ENDPOINTS =====

  /**
   * Get all blog posts (public endpoint)
   */
  async getBlogPosts(params?: {
    limit?: number;
    lastEvaluatedKey?: string;
    published?: boolean;
  }): Promise<{
    posts: BlogPost[];
    lastEvaluatedKey?: string;
    hasMore: boolean;
  }> {
    const searchParams = new URLSearchParams();
    
    if (params?.limit) {
      searchParams.append('limit', params.limit.toString());
    }
    if (params?.lastEvaluatedKey) {
      searchParams.append('lastEvaluatedKey', params.lastEvaluatedKey);
    }
    if (params?.published !== undefined) {
      searchParams.append('published', params.published.toString());
    }

    const queryString = searchParams.toString();
    const endpoint = queryString ? `/blog?${queryString}` : '/blog';
    
    return this.request<{
      posts: BlogPost[];
      lastEvaluatedKey?: string;
      hasMore: boolean;
    }>(endpoint);
  }

  /**
   * Create a new blog post (requires authentication)
   */
  async createBlogPost(formData: FormData): Promise<{ postId: string; status: string }> {
    return this.request<{ postId: string; status: string }>(
      '/blog',
      {
        method: 'POST',
        body: formData,
        headers: {}, // Don't set Content-Type for FormData
      },
      true
    );
  }

  /**
   * Update an existing blog post (requires authentication)
   */
  async updateBlogPost(postId: string, formData: FormData): Promise<{ postId: string; status: string; post: BlogPost }> {
    return this.request<{ postId: string; status: string; post: BlogPost }>(
      `/blog/${postId}`,
      {
        method: 'PUT',
        body: formData,
        headers: {}, // Don't set Content-Type for FormData
      },
      true
    );
  }

  /**
   * Delete a blog post (requires authentication)
   */
  async deleteBlogPost(postId: string, hardDelete: boolean = false): Promise<{ postId: string; status: string; hardDelete: boolean }> {
    const searchParams = new URLSearchParams();
    if (hardDelete) {
      searchParams.append('hard', 'true');
    }
    
    const queryString = searchParams.toString();
    const endpoint = queryString ? `/blog/${postId}?${queryString}` : `/blog/${postId}`;
    
    return this.request<{ postId: string; status: string; hardDelete: boolean }>(
      endpoint,
      {
        method: 'DELETE',
      },
      true
    );
  }

  // ===== VIDEO METHODS =====

  /**
   * Get videos with optional filtering and pagination
   */
  async getVideos(params?: {
    limit?: number;
    lastEvaluatedKey?: string;
    published?: boolean;
    featured?: boolean;
  }): Promise<{
    videos: Video[];
    lastEvaluatedKey?: string;
    hasMore: boolean;
  }> {
    const searchParams = new URLSearchParams();
    
    if (params?.limit) {
      searchParams.append('limit', params.limit.toString());
    }
    if (params?.lastEvaluatedKey) {
      searchParams.append('lastEvaluatedKey', params.lastEvaluatedKey);
    }
    if (params?.published !== undefined) {
      searchParams.append('published', params.published.toString());
    }
    if (params?.featured !== undefined) {
      searchParams.append('featured', params.featured.toString());
    }

    const queryString = searchParams.toString();
    const endpoint = queryString ? `/videos?${queryString}` : '/videos';
    
    return this.request<{
      videos: Video[];
      lastEvaluatedKey?: string;
      hasMore: boolean;
    }>(endpoint);
  }

  /**
   * Create a new video (requires authentication)
   */
  async createVideo(videoData: {
    title: string;
    description?: string;
    youtubeUrl: string;
    tags?: string[];
    published?: boolean;
    featured?: boolean;
  }): Promise<{ videoId: string; status: string }> {
    return this.request<{ videoId: string; status: string }>(
      '/videos',
      {
        method: 'POST',
        body: JSON.stringify(videoData),
      },
      true
    );
  }

  /**
   * Update an existing video (requires authentication)
   */
  async updateVideo(videoId: string, videoData: {
    title?: string;
    description?: string;
    youtubeUrl?: string;
    tags?: string[];
    published?: boolean;
    featured?: boolean;
  }): Promise<{ videoId: string; status: string; video: Video }> {
    return this.request<{ videoId: string; status: string; video: Video }>(
      `/videos/${videoId}`,
      {
        method: 'PUT',
        body: JSON.stringify(videoData),
      },
      true
    );
  }

  /**
   * Delete a video (requires authentication)
   */
  async deleteVideo(videoId: string): Promise<{ videoId: string; status: string }> {
    return this.request<{ videoId: string; status: string }>(
      `/videos/${videoId}`,
      {
        method: 'DELETE',
      },
      true
    );
  }

  // ===== DOROTHY (WEATHER STATION) METHODS =====

  /**
   * Get the latest weather data from the weather station (public endpoint)
   */
  async getLatestWeather(): Promise<WeatherReading> {
    return this.request<WeatherReading>('/dorothy/latest');
  }

  /**
   * Get weather history with optional pagination and date filtering (public endpoint)
   */
  async getWeatherHistory(params?: {
    limit?: number;
    nextToken?: string;
    from?: string; // ISO timestamp
    to?: string;   // ISO timestamp
    allowWeekly?: boolean; // Flag to allow weekly data (7 days)
    downsampleInterval?: number; // Downsample interval in minutes
  }): Promise<WeatherHistoryResponse> {
    const searchParams = new URLSearchParams();

    if (params?.limit) {
      searchParams.append('limit', params.limit.toString());
    }
    if (params?.nextToken) {
      searchParams.append('nextToken', params.nextToken);
    }
    if (params?.from) {
      searchParams.append('from', params.from);
    }
    if (params?.to) {
      searchParams.append('to', params.to);
    }
    if (params?.allowWeekly) {
      searchParams.append('allowWeekly', 'true');
    }
    if (params?.downsampleInterval) {
      searchParams.append('downsampleInterval', params.downsampleInterval.toString());
    }

    const queryString = searchParams.toString();
    const endpoint = queryString ? `/dorothy/history?${queryString}` : '/dorothy/history';

    return this.request<WeatherHistoryResponse>(endpoint);
  }

  async exportDorothyTelemetry(params: {
    startDate: string;
    endDate: string;
  }): Promise<ExportDorothyTelemetryResponse> {
    return this.request<ExportDorothyTelemetryResponse>(
      '/export/dorothy-telemetry',
      { method: 'POST', body: JSON.stringify(params) },
      true // requires auth
    );
  }

  async getRadarImages(params?: {
    startDate?: string; // ISO 8601
    endDate?: string;   // ISO 8601
    latest?: boolean;
  }): Promise<GetRadarImagesResponse | GetLatestRadarImageResponse> {
    const searchParams = new URLSearchParams();

    if (params?.startDate) {
      searchParams.append('startDate', params.startDate);
    }
    if (params?.endDate) {
      searchParams.append('endDate', params.endDate);
    }
    if (params?.latest) {
      searchParams.append('latest', 'true');
    }

    const queryString = searchParams.toString();
    const endpoint = queryString ? `/radar/images?${queryString}` : '/radar/images';

    return this.request<GetRadarImagesResponse | GetLatestRadarImageResponse>(
      endpoint,
      undefined,
      true // requires auth
    );
  }

  // ===== DOROTHY ALERTS METHODS =====

  /**
   * Get Dorothy weather alerts
   */
  async getDorothyAlerts(params?: {
    enabled?: boolean;
    limit?: number;
    lastEvaluatedKey?: string;
  }): Promise<{
    alerts: any[];
    lastEvaluatedKey?: string;
    hasMore: boolean;
    count: number;
  }> {
    const queryParams = new URLSearchParams();

    if (params?.enabled !== undefined) {
      queryParams.append('enabled', params.enabled.toString());
    }
    if (params?.limit) {
      queryParams.append('limit', params.limit.toString());
    }
    if (params?.lastEvaluatedKey) {
      queryParams.append('lastEvaluatedKey', params.lastEvaluatedKey);
    }

    const queryString = queryParams.toString();
    const endpoint = queryString ? `/dorothy/alerts?${queryString}` : '/dorothy/alerts';

    return this.request(endpoint, undefined, true);
  }

  /**
   * Get a single Dorothy alert by ID
   */
  async getDorothyAlert(alertId: string): Promise<any> {
    const response = await this.request(`/dorothy/alerts/${alertId}`, undefined, true) as any;
    return response.alert;
  }

  /**
   * Create a new Dorothy alert
   */
  async createDorothyAlert(alertData: {
    name: string;
    description?: string;
    enabled?: boolean;
    conditions: any[];
    conditionLogic?: 'AND' | 'OR';
    message: string;
    severity?: 'info' | 'warning' | 'critical';
    cooldownMinutes?: number;
  }): Promise<{
    status: string;
    alertId: string;
  }> {
    return this.request(
      '/dorothy/alerts',
      {
        method: 'POST',
        body: JSON.stringify(alertData),
      },
      true
    );
  }

  /**
   * Update an existing Dorothy alert
   */
  async updateDorothyAlert(alertId: string, alertData: Partial<{
    name: string;
    description?: string;
    enabled?: boolean;
    conditions: any[];
    conditionLogic?: 'AND' | 'OR';
    message: string;
    severity?: 'info' | 'warning' | 'critical';
    cooldownMinutes?: number;
    lastTriggered?: string | null;
    triggerCount?: number;
  }>): Promise<{
    status: string;
    alertId: string;
    changedFields: string[];
  }> {
    return this.request(
      `/dorothy/alerts/${alertId}`,
      {
        method: 'PUT',
        body: JSON.stringify(alertData),
      },
      true
    );
  }

  /**
   * Delete a Dorothy alert
   */
  async deleteDorothyAlert(alertId: string): Promise<{
    status: string;
    alertId: string;
  }> {
    return this.request(
      `/dorothy/alerts/${alertId}`,
      {
        method: 'DELETE',
      },
      true
    );
  }
}

// Create a singleton instance
export const apiClient = new ApiClient();

// Convenience functions for photos
export const getPhotos = (params?: {
  limit?: number;
  lastEvaluatedKey?: string;
  published?: boolean;
}) => apiClient.getPhotos(params);

export const createPhoto = (photoData: {
  title?: string;
  description: string;
  author?: string;
  published?: boolean;
  file: File;
}) => apiClient.createPhoto(photoData);

export const updatePhoto = (
  photoId: string,
  photoData: {
    title?: string;
    description?: string;
    author?: string;
    published?: boolean;
  }
) => apiClient.updatePhoto(photoId, photoData);

export const deletePhoto = (photoId: string) => apiClient.deletePhoto(photoId);

// Enhanced error handling utilities
export const handleApiError = (error: unknown): string => {
  if (error instanceof ApiClientError) {
    // Return user-friendly messages based on error type
    switch (error.type) {
      case ErrorType.NETWORK_ERROR:
        return 'Error de conexión. Por favor, verifica tu conexión a internet e intenta nuevamente.';
      case ErrorType.AUTHENTICATION_ERROR:
        return 'Error de autenticación. Por favor, inicia sesión nuevamente.';
      case ErrorType.VALIDATION_ERROR:
        // For validation errors, return the specific message if it's user-friendly,
        // otherwise return a generic validation message
        if (error.message && !error.message.includes('Request failed')) {
          return error.message;
        }
        return 'Los datos ingresados no son válidos. Por favor, revisa la información.';
      case ErrorType.SERVER_ERROR:
        return 'Error del servidor. Por favor, intenta nuevamente en unos momentos.';
      case ErrorType.TIMEOUT_ERROR:
        return 'La solicitud tardó demasiado tiempo. Por favor, intenta nuevamente.';
      default:
        return error.message || 'Ha ocurrido un error inesperado. Por favor, intenta nuevamente.';
    }
  }
  
  if (error instanceof Error) {
    return error.message;
  }
  
  return 'Ha ocurrido un error inesperado. Por favor, intenta nuevamente.';
};

// Utility to check if error is retryable by user action
export const isRetryableError = (error: unknown): boolean => {
  if (error instanceof ApiClientError) {
    return error.type === ErrorType.NETWORK_ERROR || 
           error.type === ErrorType.TIMEOUT_ERROR ||
           error.type === ErrorType.SERVER_ERROR;
  }
  return false;
};

// Utility to extract validation errors for form fields
export const extractValidationErrors = (error: unknown): Record<string, string> => {
  if (error instanceof ApiClientError && 
      error.type === ErrorType.VALIDATION_ERROR && 
      error.details) {
    
    // Assuming validation errors come in format: { fieldName: errorMessage }
    if (typeof error.details === 'object' && error.details !== null) {
      return error.details as Record<string, string>;
    }
  }
  
  return {};
};

// Utility to determine if user should be redirected to login
export const shouldRedirectToLogin = (error: unknown): boolean => {
  return error instanceof ApiClientError && 
         error.type === ErrorType.AUTHENTICATION_ERROR &&
         (error.code === 'AUTH_REQUIRED' || error.code === 'TOKEN_EXPIRED');
};

// Setup default interceptors for common functionality
export const setupDefaultInterceptors = () => {
  // Request interceptor for logging (development only)
  if (process.env.NODE_ENV === 'development') {
    apiClient.addRequestInterceptor((config) => {
      console.log(`API Request: ${config.method || 'GET'} ${config.url}`);
      return config;
    });
  }

  // Response interceptor for logging (development only)
  if (process.env.NODE_ENV === 'development') {
    apiClient.addResponseInterceptor(async (response) => {
      console.log(`API Response: ${response.status} ${response.url}`);
      return response;
    });
  }

  // Response interceptor for handling common errors
  apiClient.addResponseInterceptor(async (response) => {
    // Log server errors for monitoring
    if (response.status >= 500) {
      console.error(`Server error: ${response.status} ${response.url}`);
    }
    
    return response;
  });
};

// Initialize default interceptors
setupDefaultInterceptors();